import {
  Box,
  FormControlLabel,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { LevelChip } from '@retail/calculus/components';
import { formatPrice } from '@retail/products/utils';
import { usePurchaseAgreementsTFunction } from '@retail/purchase-agreements/i18n';
import { ItemAgreementPrices } from '@retail/purchase-agreements/types';
import { useTranslation } from 'react-i18next';
import { ScrollShadowBox } from './ScrollShadowBox';

const TWO_COLUMN_THRESHOLD = 8;

interface Props {
  itemAgreements: ItemAgreementPrices[];
  selectedAgreement?: ItemAgreementPrices;
  selectAgreement: (agreement: ItemAgreementPrices) => void;
}

export function SelectItemAgreementTable({
  itemAgreements,
  selectedAgreement,
  selectAgreement,
}: Props) {
  const t = usePurchaseAgreementsTFunction();
  const { i18n } = useTranslation();

  const selectedId = selectedAgreement?.getId();

  const twoColumns = itemAgreements.length > TWO_COLUMN_THRESHOLD;

  if (itemAgreements.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        {t('purchaseAgreements.agreement.noAvailableAgreements')}
      </Typography>
    );
  }

  return (
    <ScrollShadowBox>
      <RadioGroup
        value={selectedId ?? ''}
        onChange={(_, value) => {
          const agreement = itemAgreements.find((item) => item.getId() === value);
          if (agreement) selectAgreement(agreement);
        }}
      >
        <Box
          display="grid"
          gap={0.25}
          gridTemplateColumns={twoColumns ? { xs: '1fr', sm: 'repeat(2, 1fr)' } : '1fr'}
        >
          {itemAgreements.map((agreement) => {
            const id = agreement.getId();
            const isSelected = id === selectedId;
            const price = t('purchaseAgreements.pricePerUnit', {
              price: formatPrice(agreement.price, i18n.language),
              unit: agreement.priceUnit,
            });

            return (
              <FormControlLabel
                key={id}
                value={id}
                control={<Radio size="small" />}
                sx={{
                  m: 0,
                  px: 1,
                  py: 0.5,
                  borderRadius: 2,
                  alignItems: 'center',
                  transition: ({ transitions }) => transitions.create('background-color'),
                  '&:hover': { backgroundColor: 'action.hover' },
                  ...(isSelected && { backgroundColor: 'action.selected' }),
                  '.MuiFormControlLabel-label': { flexGrow: 1, minWidth: 0 },
                }}
                label={
                  <Stack direction="row" alignItems="center" gap={1} width="100%">
                    <ListItemText
                      primary={agreement.agreementText ?? agreement.agreementNumber}
                      secondary={`${agreement.agreementId} · ${agreement.packagePriceUnit} · ${price}`}
                      sx={{ my: 0 }}
                      slotProps={{
                        primary: { fontWeight: 500, noWrap: true },
                        secondary: { noWrap: true },
                      }}
                    />
                    {agreement.isActive && (
                      <LevelChip
                        bindingType={agreement.bindingType}
                        active
                        variant="fixed"
                        size="small"
                      />
                    )}
                  </Stack>
                }
              />
            );
          })}
        </Box>
      </RadioGroup>
    </ScrollShadowBox>
  );
}
